'use client';

import Image from '@/components/Image';
import { useAppNavigation } from '@/hooks/useAppNavigation';
import { useStore } from '@/providers/app-store-provider';
import { formatPriceNumberWithSymbol, priceInfo as getPriceInfo } from '@/utils/price';
import { useTranslations } from 'next-intl';
import { FC, memo, useEffect } from 'react';
import { useInView } from 'react-intersection-observer';
import { useFlashSaleTracking } from '../hooks/useFlashSaleTracking';
import CountDownTimer from './CountDownTimer';

// 曝光检测组件
const ExposureDetector: FC<{
  item: any;
  index: number;
  onExposure: (item: any, index: number) => void;
}> = memo(({ item, index, onExposure }) => {
  const [ref, inView] = useInView({
    threshold: 0.8,
    triggerOnce: true, // 使用 triggerOnce: true，这是正确的做法
  });

  useEffect(() => {
    if (inView && item.id) {
      onExposure(item, index);
    }
  }, [inView]);

  return <div ref={ref} className="absolute inset-0 pointer-events-none" />;
});

ExposureDetector.displayName = 'ExposureDetector';

interface FlashSaleAreaProps {
  listData: any[];
  flashSaleData: {
    title?: string;
    description?: string;
    left_image?: string;
    right_image?: string;
    sale_image?: string;
    price_word?: string;
    time_word?: string;
  };
  remainingSeconds: number;
  isCountdownVisible?: boolean; // 控制倒计时显示/隐藏
  onCountdownEnd?: () => void; // 倒计时结束回调
}

export default function FlashSaleAreaMobile({
  listData,
  flashSaleData,
  remainingSeconds,
  isCountdownVisible = true,
  onCountdownEnd,
}: FlashSaleAreaProps) {
  const { navigate } = useAppNavigation();
  const currency = useStore((state) => state.currency);
  const userInfo = useStore((state) => state.userInfo);
  const t = useTranslations('product');

  // 使用埋点追踪hook
  const { trackFlashSaleProductClick, trackFlashSaleProductExp } = useFlashSaleTracking();

  // 获取商品图片
  const getProductImage = (item: any) => {
    return item?.image?.product?.[0] || '';
  };

  // 计算商品价格信息
  const getProductPriceInfo = (item: any) => {
    return getPriceInfo(userInfo, item, currency);
  };

  // 处理商品点击
  const handleProductClick = (item: any, index: number) => {
    trackFlashSaleProductClick(item, index);
    navigate(`/goods-detail/${item.shop_sku}`);
  };

  // 商品卡片组件
  const ProductItem = ({ item, index }: { item: any; index: number }) => {
    const priceData = getProductPriceInfo(item);
    const isSoldOut = item.stock <= 0;

    return (
      <li className="w-full h-[94px] rounded-[8px] bg-white relative mb-[4px] last:mb-0">
        <a
          href={`/goods-detail/${item.shop_sku}`}
          className="flex justify-between items-center w-full h-full px-[20px] py-[14px] box-border"
          onClick={(e) => {
            e.preventDefault();
            handleProductClick(item, index);
          }}
        >
          {/* 商品图片 */}
          <Image
            src={getProductImage(item)}
            alt={item.name}
            width={120}
            height={60}
            className="w-[120px] aspect-[2/1]"
          />

          {/* 价格区域 */}
          <div className="flex flex-col">
            {/* 商品名称 */}
            <span className="text-[12px] font-bb text-[#282828] pl-[12px]">{item.short_name}</span>

            {/* 原价（如果有折扣） */}
            <span className="text-[12px] text-[#A9A9A9] line-through pl-[12px] notranslate">
              {formatPriceNumberWithSymbol(priceData.price, currency)}
            </span>

            {/* 现价盒子 */}
            <div className="flex items-center justify-between h-[28px] min-w-[132px] rounded-[9999px] overflow-hidden bg-[#ACD152] mt-[5px] relative">
              <p className="flex-1 text-[13px] font-bb text-white pl-[12px] notranslate">
                {formatPriceNumberWithSymbol(priceData.sell_price, currency)}
              </p>
              {flashSaleData.sale_image && (
                <img src={flashSaleData.sale_image} alt="" className="w-[18px] h-[28px]" />
              )}
              {flashSaleData.price_word && (
                <p className="flex items-center justify-center text-[13px] font-bb bg-[#282828] h-full min-w-[41px] text-[#FEEBE0] pr-[8px] box-border">
                  {flashSaleData.price_word}
                </p>
              )}
            </div>
          </div>

          {/* 售罄遮罩 */}
          {isSoldOut && (
            <div className="absolute inset-0 bg-white/40 rounded-[8px] cursor-default">
              <span className="absolute left-[47px] top-1/2 -translate-y-1/2 flex items-center justify-center w-[66px] aspect-square rounded-full bg-black/60 text-center">
                <div
                  className="text-[11px] text-white whitespace-pre-wrap"
                  dangerouslySetInnerHTML={{ __html: t('soldOut.soldOut') }}
                />
              </span>
            </div>
          )}
        </a>
      </li>
    );
  };

  if (!listData || listData.length === 0) {
    return null;
  }

  return (
    <div className="mx-[8px] mb-[36px] h-auto bg-[url(https://s3.zeelool.com/admin/shop/home/487f8552bfe14644a54be3b7f51703ac.png)] rounded-[8px] relative p-[10px] mt-[14px]">
      {/* 左侧装饰图片 */}
      {flashSaleData.left_image && (
        <img
          src={flashSaleData.left_image}
          alt=""
          className="w-[131px] h-auto absolute top-0 left-0"
        />
      )}

      {/* 右侧装饰图片 */}
      {flashSaleData.right_image && (
        <img
          src={flashSaleData.right_image}
          alt=""
          className="w-[131px] h-auto absolute top-0 right-0"
        />
      )}

      {/* 顶部区域 */}
      <div className="pt-[2px] pb-[12px] box-border z-10 relative">
        {/* 标题和倒计时 */}
        <div className="flex justify-between items-center text-[#282828]">
          {flashSaleData.title && <h1 className="text-[20px] font-bb">{flashSaleData.title}</h1>}

          {/* 倒计时 */}
          <CountDownTimer
            remainingSeconds={remainingSeconds}
            flashSaleData={flashSaleData}
            isVisible={isCountdownVisible}
            onCountdownEnd={onCountdownEnd}
          />
        </div>

        {/* 描述 */}
        {flashSaleData.description && (
          <h3 className="text-[12px] text-[#282828]">{flashSaleData.description}</h3>
        )}
      </div>

      {/* 商品列表 */}
      <div className="relative">
        <ul className="flash-sale-bottom-list">
          {listData.map((item, index) => (
            <div key={item.id} className="relative">
              <ProductItem item={item} index={index} />
              {/* 曝光检测组件 */}
              <ExposureDetector item={item} index={index} onExposure={trackFlashSaleProductExp} />
            </div>
          ))}
        </ul>
      </div>
    </div>
  );
}
